import { AxiosInstance } from 'axios'
import {
    Project,
    ProjectCreate,
    ProjectUpdate,
    ListProjectsParams,
    PaginatedProjectResponse,
    ProjectDetail,
    DashboardSummary,
    DashboardSummaryParams,
    ProjectExportReportPayload,
} from './project.type'
import { PaginatedUserResponse } from '../user/user.type'

export class ProjectApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all projects with pagination
     */
    async list(params?: ListProjectsParams): Promise<PaginatedProjectResponse> {
        const response = await this.client.get<PaginatedProjectResponse>('/projects', {
            params,
        })
        return response.data
    }

    /**
     * Get a single project by ID
     */
    async findOne(id: string): Promise<ProjectDetail> {
        const response = await this.client.get<ProjectDetail>(`/projects/${id}`)
        return response.data
    }

    /**
     * Create a new project
     */
    async create(payload: ProjectCreate): Promise<Project> {
        const response = await this.client.post<Project>('/projects', payload)
        return response.data
    }

    /**
     * Update a project
     */
    async update(id: string, payload: ProjectUpdate): Promise<Project> {
        const response = await this.client.put<Project>(`/projects/${id}`, payload)
        return response.data
    }

    /**
     * Delete a project (soft delete)
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/projects/${id}`)
    }

    /**
     * Apply template to an existing project
     */
    async applyTemplate(
        id: string,
        payload: { templateId: string; templateVersion: number; structure: ProjectStructure },
    ): Promise<void> {
        await this.client.post(`/projects/${id}/apply-template`, payload)
    }

    /**
     * Bulk delete projects (soft delete)
     */
    async bulkDelete(ids: string[]): Promise<void> {
        await this.client.delete('/projects', { data: { ids } })
    }

    /**
     * Get dashboard summary with date range filter
     */
    async summary(params?: DashboardSummaryParams): Promise<DashboardSummary> {
        const response = await this.client.get<DashboardSummary>('/projects/summary', {
            params,
        })
        return response.data
    }

    /**
     * Export dashboard summary as file
     */
    async exportSummary(params?: DashboardSummaryParams): Promise<Blob> {
        const response = await this.client.get('/projects/summary/export', {
            params,
            responseType: 'blob',
        })
        return response.data
    }

    /**
     * Get users scoped to a project
     */
    async listUsers(projectId: string, params?: { page?: number; take?: number }): Promise<PaginatedUserResponse> {
        const response = await this.client.get<PaginatedUserResponse>(`/projects/${projectId}/users`, {
            params,
        })
        return response.data
    }

    /**
     * Get activities scoped to a project
     */
    async getActivities(projectId: string, params?: { page?: number; take?: number }) {
        const response = await this.client.get(`/projects/${projectId}/activities`, {
            params,
        })
        return response.data
    }

    /**
     * Import projects from an Excel file
     */
    async import(file: File): Promise<void> {
        const formData = new FormData()
        formData.append('file', file)
        await this.client.post('/projects/import', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
    }

    /**
     * Export report for projects
     */
    async exportReport(payload: ProjectExportReportPayload): Promise<Blob> {
        const response = await this.client.post('/projects/export-report', payload, {
            responseType: 'blob',
        })
        return response.data
    }
}
